// frontend/src/components/ViewStockAdjustments.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Package, Plus, Building, Warehouse, Edit, Trash2, Check, X, Clock,
  TrendingUp, TrendingDown, Search, Filter, Eye, Loader2, AlertCircle,
  ChevronDown, ChevronUp, Calendar, DollarSign
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./StockAdjustmentsList.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const StockAdjustmentsList = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency: contextFormatCurrency } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Currency formatter (for future monetary fields)
  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAdjustment, setExpandedAdjustment] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
    total: 0,
    totalInQuantity: 0,
    totalOutQuantity: 0,
    netStockChange: 0
  });

  const [filters, setFilters] = useState({
    status: "",
    warehouse_id: "",
    product_id: "",
    start_date: "",
    end_date: "",
    search: ""
  });

  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';
  const canApprove = isCompanyAdmin || isSystemAdmin;

  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  const fetchAdjustments = async () => {
    try {
      setLoading(true);
      const companyId = getCompanyId();

      const params = new URLSearchParams({
        company_id: companyId,
        ...filters
      });

      const response = await axios.get(`${url}/api/stockadjustments?${params}`);

      if (response.data.success) {
        const data = response.data.data || [];
        setAdjustments(data);
        
        // Calculate detailed stats
        const pending = data.filter(a => a.status === 'pending').length;
        const approved = data.filter(a => a.status === 'approved').length;
        const rejected = data.filter(a => a.status === 'rejected').length;
        const cancelled = data.filter(a => a.status === 'cancelled').length;
        
        const totalInQuantity = data
          .filter(a => a.adjustment_type === "in" && a.status === "approved")
          .reduce((sum, a) => sum + a.quantity, 0);
          
        const totalOutQuantity = data
          .filter(a => a.adjustment_type === "out" && a.status === "approved")
          .reduce((sum, a) => sum + a.quantity, 0);
          
        const netStockChange = data
          .filter(a => a.status === "approved")
          .reduce((sum, a) => sum + (a.adjustment_type === "in" ? a.quantity : -a.quantity), 0);

        setStats({
          pending,
          approved,
          rejected,
          cancelled,
          total: data.length,
          totalInQuantity,
          totalOutQuantity,
          netStockChange
        });
      }
    } catch (error) {
      toast.error("Failed to fetch stock adjustments");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const companyId = getCompanyId();

      const params = { company_id: companyId, status: 'active' };

      const [whRes, prodRes] = await Promise.all([
        axios.get(`${url}/api/warehouse/dropdown`, { params }),
        axios.get(`${url}/api/products/dropdown`, { params })
      ]);

      if (whRes.data?.success) {
        setWarehouses(whRes.data.data || []);
      }
      if (prodRes.data?.success) {
        setProducts(prodRes.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  useEffect(() => {
    fetchAdjustments();
    fetchFilterOptions();
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    fetchAdjustments();
  };

  const handleView = (id) => {
    navigate(`/stockadjustments/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/stockadjustments/edit/${id}`);
  };

  const handleDelete = async (id, referenceId) => {
    if (!window.confirm(`Are you sure you want to delete adjustment ${referenceId}?`)) return;

    try {
      await axios.delete(`${url}/api/stockadjustments/${id}`);
      toast.success("Adjustment deleted successfully");
      fetchAdjustments();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete adjustment");
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Are you sure you want to approve this adjustment? This will update inventory.")) return;

    try {
      const res = await axios.put(`${url}/api/stockadjustments/${id}/approve`, {});

      if (res.data.success) {
        toast.success("Adjustment approved successfully");
        fetchAdjustments();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to approve adjustment");
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Please enter rejection reason:");
    if (reason === null) return;

    try {
      const res = await axios.put(`${url}/api/stockadjustments/${id}/reject`, {
        rejection_reason: reason
      });

      if (res.data.success) {
        toast.success("Adjustment rejected successfully");
        fetchAdjustments();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reject adjustment");
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this adjustment?")) return;

    try {
      const res = await axios.put(`${url}/api/stockadjustments/${id}/cancel`, {});

      if (res.data.success) {
        toast.success("Adjustment cancelled successfully");
        fetchAdjustments();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel adjustment");
    }
  };

  const toggleExpand = (id) => {
    setExpandedAdjustment(expandedAdjustment === id ? null : id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: '#fa8c16', bg: '#fff7e6', icon: <Clock size={14} />, text: 'Pending' },
      approved: { color: '#52c41a', bg: '#f6ffed', icon: <Check size={14} />, text: 'Approved' },
      rejected: { color: '#ff4d4f', bg: '#fff2f0', icon: <X size={14} />, text: 'Rejected' },
      cancelled: { color: '#999', bg: '#f5f5f5', icon: <X size={14} />, text: 'Cancelled' }
    };
    const badge = badges[status] || badges.pending;

    return (
      <span style={{
        background: badge.bg,
        color: badge.color,
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4
      }}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">
          <Loader2 className="animate-spin" size={32} />
          <p>Loading stock adjustments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-adjustments-container">
      <div className="stock-adjustments-header">
        <div>
          <h2>
            <Package size={24} /> Stock Adjustments
          </h2>
          {company && (
            <p className="company-info">
              <Building size={14} /> {company.company_name} ({company.company_code})
              <span style={{ marginLeft: '12px', fontSize: '0.8rem', color: '#666' }}>
                Currency: {companyCurrency}
              </span>
            </p>
          )}
        </div>
        <button onClick={() => navigate("/AddStockAdjustments")} className="add-adjustment-button">
          <Plus size={16} /> New Adjustment
        </button>
      </div>

      {/* Stats Summary with In/Out Breakdown */}
      <div className="stats-bar">
        <div className="stat-item">
          <Clock size={14} color="#fa8c16" />
          <span>Pending: <strong>{stats.pending}</strong></span>
        </div>
        <div className="stat-item">
          <Check size={14} color="#52c41a" />
          <span>Approved: <strong>{stats.approved}</strong></span>
        </div>
        <div className="stat-item">
          <X size={14} color="#ff4d4f" />
          <span>Rejected: <strong>{stats.rejected}</strong></span>
        </div>
        <div className="stat-item">
          <TrendingUp size={14} color="#52c41a" />
          <span>Total In: <strong>+{stats.totalInQuantity}</strong></span>
        </div>
        <div className="stat-item">
          <TrendingDown size={14} color="#ff4d4f" />
          <span>Total Out: <strong>-{stats.totalOutQuantity}</strong></span>
        </div>
        <div className="stat-item">
          <span>Net Change: </span>
          <strong style={{ 
            color: stats.netStockChange > 0 ? '#52c41a' : stats.netStockChange < 0 ? '#ff4d4f' : '#666',
            fontWeight: 600
          }}>
            {stats.netStockChange > 0 ? '+' : ''}{stats.netStockChange}
          </strong>
        </div>
        <div className="stat-item">
          <span>Total: <strong>{stats.total}</strong></span>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 10,
        marginBottom: 20,
        padding: 20,
        background: '#f5f5f5',
        borderRadius: 4
      }}>
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          style={{ padding: 8 }}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          name="warehouse_id"
          value={filters.warehouse_id}
          onChange={handleFilterChange}
          style={{ padding: 8 }}
        >
          <option value="">All Warehouses</option>
          {warehouses.map(w => (
            <option key={w._id} value={w._id}>{w.warehouse_name}</option>
          ))}
        </select>

        <select
          name="product_id"
          value={filters.product_id}
          onChange={handleFilterChange}
          style={{ padding: 8 }}
        >
          <option value="">All Products</option>
          {products.map(p => (
            <option key={p._id} value={p._id}>{p.product_name}</option>
          ))}
        </select>

        <input
          type="date"
          name="start_date"
          value={filters.start_date}
          onChange={handleFilterChange}
          placeholder="Start Date"
          style={{ padding: 8 }}
        />

        <input
          type="date"
          name="end_date"
          value={filters.end_date}
          onChange={handleFilterChange}
          placeholder="End Date"
          style={{ padding: 8 }}
        />

        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search reference, reason..."
            style={{ flex: 1, padding: 8 }}
          />
          <button
            onClick={handleSearch}
            style={{
              padding: '8px 16px',
              background: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            <Search size={16} />
          </button>
        </div>
      </div>

      <table className="stock-adjustments-table">
        <thead>
          <tr>
            <th style={{ width: '30px' }}></th>
            <th>Reference</th>
            <th>Product & Warehouse</th>
            <th>Details</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {adjustments.length === 0 ? (
            <tr>
              <td colSpan="7" className="no-data">
                <div className="no-data-message">
                  <div className="no-data-icon">📊</div>
                  No stock adjustments found
                </div>
              </td>
            </tr>
          ) : (
            adjustments.map((adj) => (
              <React.Fragment key={adj._id}>
                <tr className={`status-${adj.status}`}>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => toggleExpand(adj._id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      {expandedAdjustment === adj._id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </td>
                  <td>
                    <div className="adj-id">
                      <strong>{adj.reference_id || `ADJ${adj.adjustment_id}`}</strong>
                    </div>
                    <small className="adj-date">ID: #{adj.adjustment_id}</small>
                  </td>
                  <td>
                    <div className="adj-product">
                      <Package size={12} />
                      <strong>{adj.product_id?.product_name || "N/A"}</strong>
                    </div>
                    <div className="adj-warehouse">
                      <Warehouse size={12} />
                      <span>{adj.warehouse_id?.warehouse_name || "N/A"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="adj-details">
                      <div className="adj-type">
                        {adj.adjustment_type === 'in' ? (
                          <span className="type-in">
                            <TrendingUp size={10} /> +{adj.quantity}
                          </span>
                        ) : (
                          <span className="type-out">
                            <TrendingDown size={10} /> -{adj.quantity}
                          </span>
                        )}
                      </div>
                      {adj.reason && (
                        <div className="adj-reason">
                          <small>{adj.reason}</small>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      <Calendar size={12} style={{ display: 'inline', marginRight: 4 }} />
                      {formatDate(adj.adjustment_date)}
                    </div>
                  </td>
                  <td>{getStatusBadge(adj.status)}</td>
                  <td className="actions">
                    {adj.status === 'pending' && canApprove && (
                      <>
                        <button
                          onClick={() => handleApprove(adj._id)}
                          className="approve-btn"
                          title="Approve"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => handleReject(adj._id)}
                          className="reject-btn"
                          title="Reject"
                        >
                          <X size={14} />
                        </button>
                      </>
                    )}
                    {(adj.status === 'pending' || adj.status === 'rejected') && (
                      <>
                        <button
                          onClick={() => handleEdit(adj._id)}
                          className="edit-btn"
                          title="Edit"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(adj._id, adj.reference_id || `ADJ${adj.adjustment_id}`)}
                          className="delete-btn"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                    {adj.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(adj._id)}
                        className="delete-btn"
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleView(adj._id)}
                      className="edit-btn"
                      title="View"
                    >
                      <Eye size={14} />
                    </button>
                   </td>
                 </tr>
                {expandedAdjustment === adj._id && (
                  <tr>
                    <td colSpan="7" style={{ padding: 0, background: '#f9f9f9' }}>
                      <div style={{ padding: '20px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Adjustment Details</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                          <div>
                            <strong>Adjustment Type:</strong>{' '}
                            <span style={{ 
                              color: adj.adjustment_type === 'in' ? '#52c41a' : '#ff4d4f',
                              fontWeight: 600
                            }}>
                              {adj.adjustment_type === 'in' ? 'Stock In (+)' : 'Stock Out (-)'}
                            </span>
                          </div>
                          <div>
                            <strong>Quantity:</strong>{' '}
                            <span style={{ 
                              color: adj.adjustment_type === 'in' ? '#52c41a' : '#ff4d4f',
                              fontWeight: 600
                            }}>
                              {adj.adjustment_type === 'in' ? '+' : '-'}{adj.quantity}
                            </span>
                          </div>
                          <div>
                            <strong>Before Stock:</strong> {adj.stock_before || 0}
                          </div>
                          <div>
                            <strong>After Stock:</strong> {adj.stock_after || 0}
                          </div>
                          <div>
                            <strong>Stock Change:</strong>{' '}
                            <span style={{ 
                              color: (adj.stock_after - adj.stock_before) > 0 ? '#52c41a' : (adj.stock_after - adj.stock_before) < 0 ? '#ff4d4f' : '#666',
                              fontWeight: 600
                            }}>
                              {(adj.stock_after - adj.stock_before) > 0 ? '+' : ''}
                              {adj.stock_after - adj.stock_before}
                            </span>
                          </div>
                          <div>
                            <strong>Created By:</strong> {adj.created_by_user?.Usercomp_sure_name || 'N/A'}
                          </div>
                          <div>
                            <strong>Created At:</strong> {formatDate(adj.created_at)}
                          </div>
                          {adj.approved_by && (
                            <>
                              <div>
                                <strong>Approved By:</strong> {adj.approver?.Usercomp_sure_name || 'N/A'}
                              </div>
                              <div>
                                <strong>Approved At:</strong> {formatDate(adj.approved_at)}
                              </div>
                            </>
                          )}
                          {adj.rejected_by && (
                            <>
                              <div>
                                <strong>Rejected By:</strong> {adj.rejecter?.Usercomp_sure_name || 'N/A'}
                              </div>
                              <div>
                                <strong>Rejected At:</strong> {formatDate(adj.rejected_at)}
                              </div>
                            </>
                          )}
                          {adj.rejection_reason && (
                            <div style={{ gridColumn: 'span 2' }}>
                              <strong>Rejection Reason:</strong> {adj.rejection_reason}
                            </div>
                          )}
                          {adj.notes && (
                            <div style={{ gridColumn: 'span 2' }}>
                              <strong>Notes:</strong> {adj.notes}
                            </div>
                          )}
                          {adj.inventory_transaction_id && (
                            <div style={{ gridColumn: 'span 2' }}>
                              <strong>Inventory Transaction ID:</strong> {adj.inventory_transaction_id}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                   </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default StockAdjustmentsList;